"use client";

import Image from "next/image";
import { Check, Minus } from "lucide-react";
import { useState } from "react";

import { CcFaqPillTabs } from "@/components/ui/CcFaqPillTabs";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { CcSectionReveal } from "@/components/ui/CcSectionReveal";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { EcommercePlatform, EcommerceSubpageCopy } from "./ecommerce-page-copy";

function PlatformMark({ platform, size = 18 }: { platform: EcommercePlatform; size?: number }) {
  return platform.logo ? (
    <Image src={platform.logo} alt="" width={size} height={size} unoptimized />
  ) : (
    <span className="cc-ecom-platforms__badge" aria-hidden="true">
      {platform.badge ?? platform.name.charAt(0)}
    </span>
  );
}

/** A store-admin window for the chosen platform, with the services it's wired to. */
function AdminWindow({
  platform,
  windowLabel,
  connectedLabel,
}: {
  platform: EcommercePlatform;
  windowLabel: string;
  connectedLabel: string;
}) {
  return (
    <div className="cc-ecom-admin" aria-hidden="true">
      <div className="cc-ecom-admin__chrome">
        <span className="cc-ecom-admin__dots">
          <i />
          <i />
          <i />
        </span>
        <span className="cc-ecom-admin__title">
          <PlatformMark platform={platform} size={14} />
          {platform.name} · {windowLabel}
        </span>
      </div>
      <div className="cc-ecom-admin__body">
        <div className="cc-ecom-admin__metrics">
          {platform.metrics.map((label, index) => (
            <div key={label} className="cc-ecom-admin__metric">
              <small>{label}</small>
              <strong>{platform.values[index]}</strong>
            </div>
          ))}
        </div>
        <div className="cc-ecom-admin__chart">
          {[38, 52, 44, 63, 58, 72, 80].map((height, index) => (
            <i key={index} style={{ height: `${height}%` }} />
          ))}
        </div>
        <div className="cc-ecom-admin__rows">
          {[0, 1, 2].map((row) => (
            <span key={row} className="cc-ecom-admin__row">
              <i />
              <b />
              <em />
            </span>
          ))}
        </div>
      </div>
      <div className="cc-ecom-admin__integrations">
        <small>{connectedLabel}</small>
        {platform.integrations.map((logo) => (
          <span key={logo} className="cc-ecom-admin__integration">
            <Image src={logo} alt="" width={16} height={16} unoptimized />
          </span>
        ))}
      </div>
    </div>
  );
}

type CcEcomPlatformTabsProps = {
  copy: EcommerceSubpageCopy["platforms"];
};

/**
 * "Which e-commerce platform fits you?" Tabs per platform, each with when it
 * fits, strengths, trade-offs and a store-admin window, after Voiceflow's
 * tabbed platform overview. All three panels are in the HTML for search
 * engines; only the active one is shown.
 */
export function CcEcomPlatformTabs({ copy }: CcEcomPlatformTabsProps) {
  const [activeId, setActiveId] = useState(copy.items[0]?.id ?? "");

  return (
    <CcSectionReveal
      className="cc-section cc-ecom-platforms"
      id="ecom-platforms"
      aria-labelledby="cc-ecom-platforms-title"
      data-cc-nav="light"
      staggerChildren
      {...devSectionAttr("ecom-platforms")}
    >
      <div className="cc-ecom-platforms__container">
        <header className="cc-ecom-platforms__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-ecom-platforms-title" className="cc-ecom-platforms__title">
            {copy.title}
          </h2>
          <p className="cc-ecom-platforms__intro">{copy.intro}</p>
        </header>

        <div className="cc-ecom-platforms__tabs cc-faq-light-tabs">
          <CcFaqPillTabs
            items={copy.items.map((platform) => ({
              id: platform.id,
              tabId: `cc-ecom-platform-tab-${platform.id}`,
              label: (
                <>
                  <PlatformMark platform={platform} size={14} />
                  <span>{platform.name}</span>
                </>
              ),
            }))}
            activeId={activeId}
            onSelect={setActiveId}
            ariaLabel={copy.tablistLabel}
          />
        </div>

        {copy.items.map((platform) => (
          <div
            key={platform.id}
            id={`cc-ecom-platform-panel-${platform.id}`}
            role="tabpanel"
            aria-labelledby={`cc-ecom-platform-tab-${platform.id}`}
            className="cc-ecom-platforms__panel"
            hidden={platform.id !== activeId}
          >
            <div className="cc-ecom-platforms__copy">
              <h3 className="cc-ecom-platforms__name">
                <PlatformMark platform={platform} size={22} />
                {platform.name}
              </h3>
              <p className="cc-ecom-platforms__tagline">{platform.tagline}</p>
              <p className="cc-ecom-platforms__fit">
                <strong>{copy.fitLabel}:</strong> {platform.fit}
              </p>
              <div className="cc-ecom-platforms__lists">
                <div>
                  <h4>{copy.prosLabel}</h4>
                  <ul>
                    {platform.pros.map((item) => (
                      <li key={item}>
                        <Check size={14} strokeWidth={2.2} aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4>{copy.consLabel}</h4>
                  <ul>
                    {platform.cons.map((item) => (
                      <li key={item} data-tone="muted">
                        <Minus size={14} strokeWidth={2.2} aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="cc-ecom-platforms__visual">
              <AdminWindow
                platform={platform}
                windowLabel={copy.windowLabel}
                connectedLabel={copy.connectedLabel}
              />
            </div>
          </div>
        ))}
      </div>
    </CcSectionReveal>
  );
}
